import { Image } from 'antd';
import { useTitle } from 'contexts/TitleProvider';
import Content from 'layouts/Content';
import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import formatNumber, { formatDate } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';

const { FileImageOutlined } = icons;

export default function ProductDetail() {
  const location = useLocation();
  const product = location.state;
  const { setTitle, setBreadcrumb } = useTitle();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const listImages = useMemo(() => {
    if (product?.tproducts) {
      return [
        product?.tproducts?.urlImage1,
        product?.tproducts?.urlImage2,
        product?.tproducts?.urlImage3,
        product?.tproducts?.urlImage4,
        product?.tproducts?.urlImage5,
      ].filter((image) => image);
    } else {
      return [
        product?.urlImage1,
        product?.urlImage2,
        product?.urlImage3,
        product?.urlImage4,
        product?.urlImage5,
      ].filter((image) => image);
    }
  }, [product]);

  const [mainPic, setMainPic] = useState(
    product?.tproducts?.urlImage1 || product?.urlImage1,
  );
  const [imageLoadError, setImageLoadError] = useState<any>({});

  const handleImageError = (index: string) => {
    setImageLoadError((prev: any) => ({ ...prev, [index]: true }));
  };

  const isInventoryProduct = Boolean(product?.sloc);

  useEffect(() => {
    const title = `${product?.tproducts?.name || product?.name}`;
    setTitle(isInventoryProduct ? 'Sản phẩm tồn kho theo sloc' : 'Sản phẩm');
    setBreadcrumb([
      { title: 'Sản phẩm' },
      {
        title: isInventoryProduct ? 'Sản phẩm tồn kho theo sloc' : 'Sản phẩm',
        path: '',
        onClick: (e: MouseEvent) => {
          e.preventDefault();
          navigate(-1);
        },
      },
      { title: title || '-' },
    ]);
  }, [
    navigate,
    searchParams,
    setBreadcrumb,
    setTitle,
    isInventoryProduct,
    product,
  ]);

  return (
    <Content>
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-8 px-4 md:px-8 py-8"
        style={{ justifyContent: 'start' }}
      >
        <div className="flex flex-col gap-4">
          <div className="flex justify-center items-center bg-neutral-95 rounded-lg">
            {imageLoadError['main'] ? (
              <div
                style={{
                  width: '100%',
                  height: '350px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: '#f0f0f0',
                }}
              >
                <FileImageOutlined
                  style={{ fontSize: '64px', color: '#999' }}
                />
              </div>
            ) : (
              <Image
                className="product-image-item active"
                width={'100%'}
                height={'350px'}
                preview={true}
                src={mainPic ? mainPic : ''}
                onError={() => handleImageError('main')}
              />
            )}
          </div>

          <div className="grid grid-cols-5 gap-2">
            {listImages.map((image, index) =>
              !image ? (
                <FileImageOutlined key={index} />
              ) : (
                <Image
                  key={index}
                  className="product-image-item bg-neutral-95 border border-solid rounded-lg active ml cursor-pointer"
                  height={'80px'}
                  width={'100%'}
                  preview={false}
                  src={image}
                  onClick={() => setMainPic(image)}
                />
              ),
            )}
          </div>
        </div>

        <div>
          <div className="text-primary-50 font-bold text-xl mb-4">
            {isInventoryProduct
              ? `[${product?.tproducts?.id}] ${product?.tproducts?.name}`
              : `[${product?.id}] ${product?.name}`}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-bold text-lg">Mã sản phẩm</div>
              <div className="text-sm">
                {isInventoryProduct
                  ? `${product?.tproducts?.id || '-'}`
                  : `${product?.id || '-'}`}
              </div>
            </div>

            <div>
              <div className="font-bold text-lg">
                {isInventoryProduct ? 'Tổng TL vàng (chỉ)' : 'Bộ sưu tập'}
              </div>
              <div className="text-sm">
                {isInventoryProduct
                  ? product?.goldenWeightThread || '-'
                  : product?.collection || '-'}
              </div>
            </div>

            <div>
              <div className="font-bold text-lg">
                {isInventoryProduct ? 'Số batch' : 'Dòng hàng SAP'}
              </div>
              <div className="text-sm">
                {isInventoryProduct
                  ? product?.batch || '-'
                  : product?.commodityLine || '-'}
              </div>
            </div>

            <div>
              <div className="font-bold text-lg">
                {isInventoryProduct ? 'Tổng TL món (chỉ)' : 'Giá hao lai'}
              </div>
              <div className="text-sm">
                {isInventoryProduct ? product?.itemWeightThread || '-' : '-'}
              </div>
            </div>

            <div>
              <div className="font-bold text-lg">
                {isInventoryProduct ? 'Số sloc' : 'Loại sản phẩm'}
              </div>
              <div className="text-sm">
                {isInventoryProduct
                  ? product?.sloc || '-'
                  : product?.type || '-'}
              </div>
            </div>

            <div>
              <div className="font-bold text-lg">
                {isInventoryProduct ? 'Tổng TL món (gram)' : 'Mã MRP'}
              </div>
              <div className="text-sm">
                {isInventoryProduct
                  ? product?.itemWeightGram || '-'
                  : product?.mrpId || '-'}
              </div>
            </div>

            <div>
              <div className="font-bold text-lg">
                {isInventoryProduct ? 'Số lượng' : 'Chủng loại'}
              </div>
              <div className="text-sm">
                {isInventoryProduct
                  ? product?.totalInventory || '-'
                  : product?.ttype?.name || '-'}
              </div>
            </div>

            <div>
              <div className="font-bold text-lg">
                {isInventoryProduct ? 'Nhóm giá (vnđ)' : 'Tên sản phẩm MRP'}
              </div>
              <div className="text-sm">
                {isInventoryProduct
                  ? product?.groupPrice || '-'
                  : product?.mrpName || '-'}
              </div>
            </div>

            <div>
              <div className="font-bold text-lg">Tuổi vàng</div>
              <div className="text-sm">
                {isInventoryProduct
                  ? `${product?.tproducts?.age || '-'}`
                  : `${product?.age || '-'}`}
              </div>
            </div>

            {isInventoryProduct && (
              <div>
                <div className="font-bold text-lg">Đơn giá bán (vnđ/gram)</div>
                <div className="text-sm">
                  {formatNumber(product?.unitPriceByGram) || '-'}
                </div>
              </div>
            )}

            {/* {!isInventoryProduct && <div />} */}

            <div>
              <div className="font-bold text-lg">Đơn vị tính</div>
              <div className="text-sm">
                {isInventoryProduct
                  ? `${product?.tproducts?.unit || '-'}`
                  : `${product?.unit || '-'}`}
              </div>
            </div>

            {isInventoryProduct && (
              <>
                <div>
                  <div className="font-bold text-lg">
                    Đơn giá bán (vnđ/phân)
                  </div>
                  <div className="text-sm">
                    {formatNumber(product?.unitPriceByInch) || '-'}
                  </div>
                </div>

                <div>
                  <div className="font-bold text-lg">Giá công (vnđ)</div>
                  <div className="text-sm">
                    {formatNumber(product?.processPrice) || '-'}
                  </div>
                </div>

                {/* {isInventoryProduct && <div />} */}

                <div>
                  <div className="font-bold text-lg">Thành tiền (vnđ)</div>
                  <div className="text-sm">
                    {formatNumber(product?.totalPrice) || '-'}
                  </div>
                </div>

                {/* {isInventoryProduct && <div />} */}

                <div>
                  <div className="font-bold text-lg">MC Group</div>
                  <div className="text-sm">{product?.mcGroup || '-'}</div>
                </div>
              </>
            )}
            {!isInventoryProduct && <div />}

            <div>
              <div className="font-bold text-lg">Người cập nhật</div>
              <div className="text-sm">
                {product?.tmodifyUser?.fullName || '-'}
              </div>
            </div>
            {<div />}

            <div>
              <div className="font-bold text-lg">Ngày cập nhật</div>
              <div className="text-sm">
                {formatDate(product?.modifyDate) || '-'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Content>
  );
}
